import { useEffect } from "react";
import ExcelJS from "exceljs";
import { headColor } from "../content/dashBoard/businessAnalysis";

/**
 * Automatically adjusts the element's height to fill available vertical space.
 * Works with any container or component that has a ref.
 *
 * @param {React.RefObject} ref - Element reference
 * @param {number} [offset=20] - Bottom offset (for padding or margins)
 */
export const useAutoResizeHeight = (ref, offset = 20, autoResize = true) => {
    useEffect(() => {
        const updateHeight = () => {
            if (!ref.current) return;
            const rect = ref.current.getBoundingClientRect();
            const availableHeight = window.innerHeight - rect.top - offset;
            ref.current.style.maxHeight = `${availableHeight}px`;
        };
        if (autoResize) {
            updateHeight();
        }
        if (autoResize) {
            window.addEventListener("resize", updateHeight);
        }
        return () => {
            if (autoResize) {
                window.removeEventListener("resize", updateHeight);
            }
        };
    }, [ref, offset, autoResize]);
};

const BORDER = {
    top: { style: "thin", color: { argb: "FFAAAAAA" } },
    left: { style: "thin", color: { argb: "FFAAAAAA" } },
    bottom: { style: "thin", color: { argb: "FFAAAAAA" } },
    right: { style: "thin", color: { argb: "FFAAAAAA" } },
};
function applyHeaderStyles(row, headers, headerColor) {
    row.font = { bold: true };
    row.alignment = { vertical: "middle", horizontal: "center" };
    row.eachCell((cell, colNumber) => {
        cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: headerColor.replace("#", "FF") },
        };
        cell.border = BORDER;
        const headerDef = headers[colNumber - 1];
        if (headerDef?.textAlign) {
            cell.alignment = {
                vertical: "middle",
                horizontal: headerDef.textAlign,
            };
        }
    });
}

function applyDataRowStyles(row, fillColor) {
    row.eachCell((cell) => {
        cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: fillColor.replace("#", "FF") },
        };
        cell.border = BORDER;
    });
}

function applyColSpan(worksheet, headers, row, colSpan) {
    if (!colSpan) return;
    Object.entries(colSpan).forEach(([field, span]) => {
        if (span > 1) {
            const start = headers.findIndex((h) => h.field === field) + 1;
            const end = start + span - 1;
            try{
                worksheet.mergeCells(row.number, start, row.number, end);
            }catch(err){

            }
        }
    });
}

function applyAlignment(headers, row, align) {
    if (!align) return;
    Object.entries(align).forEach(([field, alignment]) => {
        const colIdx = headers.findIndex((h) => h.field === field);
        if (colIdx !== -1) {
            row.getCell(colIdx + 1).alignment = {
                vertical: "middle",
                horizontal: alignment,
            };
        }
    });
}

function autoResizeColumns(worksheet, headers) {
    const colCount = headers.length;
    for (let col = 1; col <= colCount; col++) {
        let maxLength = 10;
        worksheet.getColumn(col).eachCell({ includeEmpty: true }, (cell) => {
            if (!cell.isMerged) {
                const v = cell.value ? cell.value.toString() : "";
                if (v.length > maxLength) maxLength = v.length;
            }
        });
        worksheet.getColumn(col).width = maxLength + 2;
    }
}
function addComplexHeader(worksheet, headerRows, startRow, headerColor) {
    const mainHeaderRow = headerRows.find((h) => h?.[0]?.mainHeader === true)
    headerRows.forEach((headerRow, rIndex) => {
        const row = worksheet.getRow(startRow + rIndex);
        const filteredCells = headerRow;
        let col = 1;
        filteredCells.forEach((cell) => {
            const target = row.getCell(col);
            target.value = cell.header;
            target.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: headerColor.replace("#", "FF") },
            };
            if (cell.span && cell.span > 1) {
                worksheet.mergeCells(
                    startRow + rIndex,
                    col,
                    startRow + rIndex,
                    col + cell.span - 1
                );
            }
            if (cell.rowSpan && cell.rowSpan > 1) {
                worksheet.mergeCells(
                    startRow + rIndex,
                    col,
                    startRow + rIndex + cell.rowSpan - 1,
                    col
                );
            }
            col += cell.span || 1;
        });
        applyHeaderStyles(row, mainHeaderRow, headerColor);
        row.commit();
    });
    
}

export async function exportToExcel({
    fileName = "data.xlsx",
    sheetName = "Sheet1",
    headersList = [],
    dataList = [],
}) {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(sheetName);
    let currentRow = 1;
    for (let t = 0; t < headersList.length; t++) {
        const headerData = headersList[t];
        const isComplex = Array.isArray(headerData[0]);
        const headers = isComplex ? headerData.find((h) => h?.[0]?.mainHeader === true) : headerData;
        const skipSpace = isComplex ? !!headers?.[0].skipSpace : false;
        const data = dataList[t];
        if (isComplex) {
            addComplexHeader(worksheet, headerData, currentRow, headColor.header);
            currentRow += headerData.length;
        } else if (!headers?.[0]?.hideHeader) {
            const headerRow = worksheet.getRow(currentRow);
            headerRow.values = headers.map(h => h.header);
            applyHeaderStyles(headerRow, headers, headColor.header);
            currentRow++;
        }
        for (const rowObj of data) {
            const { dataType, colSpan, align } = rowObj;
            const rowData = headers.map((h) => rowObj[h.field] ?? "");
            const alignment = headers.reduce((acc, { field, textAlign }) => {
                if (['center', 'right'].includes(textAlign)) {
                    acc[field] = textAlign;
                }
                return acc;
            }, {});
            const row = worksheet.getRow(currentRow++);
            row.values = rowData;
            const fillColor = headColor[dataType] || "#ffffff";
            applyDataRowStyles(row, fillColor);
            applyColSpan(worksheet, headers, row, colSpan);
            applyAlignment(headers, row, { ...alignment, ...align });
        }
        if (!skipSpace) {
            currentRow += 2;
        }
        autoResizeColumns(worksheet, headers);
    }
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(link.href);
}

